import { useState } from "react";
import { archiveReflection, unarchiveReflection } from "@/lib/supabase";
import type { Reflection } from "../types";

interface UseReflectionActionsOptions {
	reflectionId: string | undefined;
	userId: string | undefined;
	setReflection: React.Dispatch<React.SetStateAction<Reflection | null>>;
}

interface UseReflectionActionsReturn {
	archiveLoading: boolean;
	showArchiveDialog: boolean;
	setShowArchiveDialog: (show: boolean) => void;
	showUnarchiveDialog: boolean;
	setShowUnarchiveDialog: (show: boolean) => void;
	handleArchive: () => Promise<void>;
	handleUnarchive: () => Promise<void>;
}

export function useReflectionActions({
	reflectionId,
	userId,
	setReflection,
}: UseReflectionActionsOptions): UseReflectionActionsReturn {
	const [archiveLoading, setArchiveLoading] = useState(false);
	const [showArchiveDialog, setShowArchiveDialog] = useState(false);
	const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false);

	const handleArchive = async () => {
		if (!reflectionId || !userId) return;

		try {
			setArchiveLoading(true);
			const { error } = await archiveReflection(reflectionId, userId);

			if (error) throw error;

			setReflection((prev) => (prev ? { ...prev, archived: true } : null));
			setShowArchiveDialog(false);
		} catch (error) {
			console.error("Error archiving reflection:", error);
			alert("Failed to archive reflection. Please try again.");
		} finally {
			setArchiveLoading(false);
		}
	};

	const handleUnarchive = async () => {
		if (!reflectionId || !userId) return;

		try {
			setArchiveLoading(true);
			const { error } = await unarchiveReflection(reflectionId, userId);

			if (error) throw error;

			setReflection((prev) => (prev ? { ...prev, archived: false } : null));
			setShowUnarchiveDialog(false);
		} catch (error) {
			console.error("Error unarchiving reflection:", error);
			alert("Failed to unarchive reflection. Please try again.");
		} finally {
			setArchiveLoading(false);
		}
	};

	return {
		archiveLoading,
		showArchiveDialog,
		setShowArchiveDialog,
		showUnarchiveDialog,
		setShowUnarchiveDialog,
		handleArchive,
		handleUnarchive,
	};
}
